import { useEffect, useRef, useState } from "react";
import { generateFaceFromSelfie } from "./api";
import { CameraCapture, type CapturedSelfie } from "./CameraCapture";
import {
  IconDice,
  IconDownload,
  IconLink,
  IconRefresh,
  IconShare,
  IconSparkles,
  IconX,
} from "./icons";
import { copyLink, saveImage, shareToX } from "./share";
import { ShareSheet } from "./ShareSheet";
import type { FaceTraits } from "./types";

type Phase = "idle" | "loading" | "result" | "error";

const LOADING_LINES = [
  "셀카 확인 중…",
  "10억 입금 중…",
  "얼굴 추첨 중…",
  "두구두구두구…",
] as const;

export function App() {
  const [selfie, setSelfie] = useState<CapturedSelfie | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingLineIndex, setLoadingLineIndex] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [cachedTraits, setCachedTraits] = useState<FaceTraits | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  const canSpin = selfie !== null && phase !== "loading";

  const resultRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (phase === "result") {
      resultRef.current?.focus();
    } else if (phase === "error") {
      errorRef.current?.focus();
    }
  }, [phase]);

  async function handleSave() {
    if (!imageUrl) return;
    await saveImage(imageUrl);
    setToast("이미지를 저장했어요");
  }

  async function handleCopyLink() {
    setToast((await copyLink()) ? "링크를 복사했어요" : "복사에 실패했어요");
  }

  async function handleSpin() {
    if (!selfie) return;

    setPhase("loading");
    setError(null);
    setImageUrl(null);
    setShareOpen(false);
    setLoadingLineIndex(0);

    const lineTimer = window.setInterval(() => {
      setLoadingLineIndex((prev) => (prev + 1) % LOADING_LINES.length);
    }, 1600);

    try {
      const result = await generateFaceFromSelfie(
        selfie.base64,
        selfie.mimeType,
        cachedTraits,
      );
      if (!result.ok) {
        setError(result.error);
        setPhase("error");
        return;
      }
      setCachedTraits(result.traits);
      setImageUrl(result.imageUrl);
      setPhase("result");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "알 수 없는 오류";
      setError(message);
      setPhase("error");
    } finally {
      window.clearInterval(lineTimer);
    }
  }

  function retakeSelfie() {
    setSelfie(null);
    setCachedTraits(null);
    setPhase("idle");
    setImageUrl(null);
    setError(null);
    setShareOpen(false);
  }

  return (
    <div className="page">
      <a className="skip-link" href="#main">
        본문으로 건너뛰기
      </a>
      <main className="shell" id="main">
        <header className="hero">
          <p className="badge">
            <IconSparkles size={15} />
            <span>10억 얼굴 룰렛</span>
          </p>
          <h1>10억 받고 얼굴 돌리기</h1>
          <p className="sub">셀카 찍고 버튼 한 번. 운에 맡긴 새 얼굴이 나옵니다.</p>
        </header>

        <section className="panel">
          {phase !== "result" && (
            <CameraCapture
              disabled={phase === "loading"}
              capturedDataUrl={selfie?.dataUrl ?? null}
              onCapture={(captured) => {
                setSelfie(captured);
                setCachedTraits(null);
              }}
              onClear={() => {
                setSelfie(null);
                setCachedTraits(null);
              }}
            />
          )}

          {phase !== "result" && (
            <button
              type="button"
              className="spin-btn"
              onClick={() => void handleSpin()}
              disabled={!canSpin}
            >
              {phase === "loading" ? (
                "돌리는 중…"
              ) : selfie ? (
                <>
                  <IconDice size={20} />
                  <span>얼굴 돌리기</span>
                </>
              ) : (
                "셀카부터 찍어주세요"
              )}
            </button>
          )}

          {phase === "loading" && (
            <div className="status loading" role="status" aria-live="polite">
              <div className="spinner" aria-hidden />
              <p>{LOADING_LINES[loadingLineIndex]}</p>
            </div>
          )}

          {phase === "error" && error && (
            <div className="status error" role="alert" ref={errorRef} tabIndex={-1}>
              <p className="error-title">앗, 실패했어요</p>
              <p>{error}</p>
              <button type="button" className="linkish" onClick={() => setPhase("idle")}>
                다시 시도
              </button>
            </div>
          )}

          {phase === "result" && imageUrl && (
            <div className="result" ref={resultRef} tabIndex={-1}>
              <div className="result-head">
                <span className="result-eyebrow">
                  <IconSparkles size={14} />
                  <span>새로 뽑은 얼굴</span>
                </span>
                <p>같은 조건으로 다시 돌리면 매번 다른 사람이 나와요.</p>
              </div>

              <div className="photo-frame">
                <img src={imageUrl} alt="랜덤 생성된 얼굴" className="face-photo" />
                {selfie && (
                  <div className="photo-orig">
                    <img src={selfie.dataUrl} alt="원본 셀카" />
                    <span>원본</span>
                  </div>
                )}
              </div>

              <div className="share-row">
                <button
                  type="button"
                  className="share-btn primary"
                  onClick={() => setShareOpen(true)}
                >
                  <IconShare size={18} />
                  <span>공유</span>
                </button>
                <button
                  type="button"
                  className="share-btn"
                  onClick={() => void handleSave()}
                >
                  <IconDownload size={18} />
                  <span>저장</span>
                </button>
                <button
                  type="button"
                  className="share-btn icon-only"
                  onClick={shareToX}
                  aria-label="X에 공유"
                >
                  <IconX size={17} />
                </button>
                <button
                  type="button"
                  className="share-btn icon-only"
                  onClick={() => void handleCopyLink()}
                  aria-label="링크 복사"
                >
                  <IconLink size={18} />
                </button>
              </div>

              <div className="result-actions">
                <button
                  type="button"
                  className="spin-btn"
                  onClick={() => void handleSpin()}
                >
                  <IconDice size={20} />
                  <span>한 판 더</span>
                </button>
                <button type="button" className="secondary-btn" onClick={retakeSelfie}>
                  <IconRefresh size={18} />
                  <span>새 셀카로</span>
                </button>
              </div>
            </div>
          )}
        </section>
      </main>

      {shareOpen && imageUrl && (
        <ShareSheet
          faceUrl={imageUrl}
          selfieUrl={selfie?.dataUrl ?? null}
          onClose={() => setShareOpen(false)}
          onToast={setToast}
        />
      )}

      {toast && (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}
